import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { organisationAdminService } from '../services/organisationAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { Spinner } from '../components/ui/Spinner'
import { Badge } from '../components/ui/Badge'

function formatDateTime(isoOrDate) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function formatOptionalRange(min, max, formatter = (value) => value) {
  if (min == null && max == null) return '—'
  if (min != null && max != null) return `${formatter(min)} – ${formatter(max)}`
  if (min != null) return `${formatter(min)}+`
  return `Up to ${formatter(max)}`
}

function formatNumber(value) {
  return Number(value).toLocaleString()
}

function TagList({ items, emptyText }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">{emptyText}</p>
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => (
        <Badge key={`${item}-${index}`} variant="primary">
          {item}
        </Badge>
      ))}
    </div>
  )
}

const OrganisationWorkflowConfigDetail = () => {
  const { organisationId, workflowConfigId } = useParams()
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    setNotFound(false)
    try {
      const res = await organisationAdminService.getOrganisationWorkflowConfig(
        organisationId,
        workflowConfigId
      )
      const data = res?.data ?? null
      if (!data) {
        setNotFound(true)
        return
      }
      setConfig(data)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [organisationId, workflowConfigId])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }

  if (notFound || !config) {
    return (
      <div>
        <PageHeader
          title="Workflow config not found"
          breadcrumbs={[
            { label: 'Organisations', href: '/admin/organisations' },
            { label: 'Organisation', href: `/admin/organisations/${organisationId}` },
            { label: 'Not found' },
          ]}
        />
        <div className="card text-text-muted">This workflow config could not be loaded.</div>
      </div>
    )
  }

  const domains = config.domains || []
  const locations = config.locations || []
  const designations = config.designations || []
  const instructions = config.custom_instructions || []

  return (
    <div>
      <PageHeader
        title={config.name || 'Untitled configuration'}
        subtitle={`${config.company_name || 'No company'}${config.created_by_name ? ` · ${config.created_by_name}` : ''} · Updated ${formatDateTime(config.updated_at || config.created_at)}`}
        breadcrumbs={[
          { label: 'Organisations', href: '/admin/organisations' },
          { label: 'Organisation', href: `/admin/organisations/${organisationId}` },
          { label: config.name || 'Config' },
        ]}
      />

      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="font-medium text-gray-500">Config ID</dt>
            <dd className="mt-1 font-mono text-gray-900 break-all">{config.workflow_config_id}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Website</dt>
            <dd className="mt-1 text-gray-900">
              {config.company_website ? (
                <a
                  href={
                    config.company_website.startsWith('http')
                      ? config.company_website
                      : `https://${config.company_website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 hover:underline break-all"
                >
                  {config.company_website}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Min score</dt>
            <dd className="mt-1 text-gray-900">{config.minimum_score != null ? config.minimum_score : 70}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Target leads</dt>
            <dd className="mt-1 text-gray-900">{config.leads_count ?? '—'}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Headcount</dt>
            <dd className="mt-1 text-gray-900">
              {formatOptionalRange(config.headcount_min, config.headcount_max, formatNumber)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Revenue</dt>
            <dd className="mt-1 text-gray-900">
              {formatOptionalRange(config.revenue_min, config.revenue_max, (value) => `$${formatNumber(value)}`)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Domains ({domains.length})</h3>
          <TagList items={domains} emptyText="No domains specified." />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Locations ({locations.length})</h3>
          <TagList items={locations} emptyText="No location filter." />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Roles ({designations.length})</h3>
          <TagList items={designations} emptyText="No role filters specified." />
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Custom instructions</h2>
        {instructions.length > 0 ? (
          <ul className="space-y-3">
            {instructions.map((instruction, index) => (
              <li key={index} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
                {instruction}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No extra instructions.</p>
        )}
      </div>
    </div>
  )
}

export default OrganisationWorkflowConfigDetail
